import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TbPlus, TbEdit, TbTrash, TbCategory, TbLock } from 'react-icons/tb';

import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import ModalLayout from '../../components/layouts/ModalLayout';
import Input from '../../components/misc/Input';
import Labeled from '../../components/misc/Labeled';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import CreateCategoryModal from '../../components/financial/CreateCategoryModal';
import {
  FinancialCategory,
  UpdateFinancialCategoryDto,
} from '../../../types/bankAccount';
import * as bankAccountService from '../../../services/community/bankAccountService';

interface CategoriesPageProps {
  hideHeader?: boolean;
}

const COLOR_OPTIONS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#6366F1',
  '#14B8A6',
  '#6B7280',
  '#0EA5E9',
];

export default function CategoriesPage({ hideHeader = false }: CategoriesPageProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.categories' });

  const [categories, setCategories] = useState<FinancialCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FinancialCategory | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bankAccountService.getCategories();
      setCategories(data);
    } catch (error: unknown) {
      const message = (error as Error)?.message ?? String(error);
      toast.error(message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCategoryCreated = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleEditCategory = async () => {
    if (!editingCategory || !editingCategory.name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }
    try {
      const dto: UpdateFinancialCategoryDto = {
        name: editingCategory.name.trim(),
        description: editingCategory.description?.trim() || null,
        color: editingCategory.color,
      };
      await bankAccountService.updateCategory(editingCategory.financialCategoryId, dto);
      toast.success(t('success.updated'));
      setEditingCategory(null);
      setIsEditModalOpen(false);
      refresh();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message ??
        (error as Error)?.message ??
        String(error);
      toast.error(message);
    }
  };

  const handleDeleteCategory = async (category: FinancialCategory) => {
    if (window.confirm(t('confirmations.delete', { name: category.name }))) {
      try {
        await bankAccountService.deleteCategory(category.financialCategoryId);
        toast.success(t('success.deleted'));
        refresh();
      } catch (error: unknown) {
        const message = (error as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ??
          (error as Error)?.message ??
          String(error);
        toast.error(message);
      }
    }
  };

  const openEditModal = (category: FinancialCategory) => {
    if (category.isSystem) return;
    setEditingCategory({ ...category });
    setIsEditModalOpen(true);
  };

  const sortedCategories = useMemo(() => categories, [categories]);

  return (
    <>
      {!hideHeader && (
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          primaryButton={
            <Button variant="primary-dark" onClick={() => setIsCreateModalOpen(true)}>
              <TbPlus />
              {t('addCategory')}
            </Button>
          }
        />
      )}

      {hideHeader && (
        <div className="mb-4 flex justify-end">
          <Button variant="primary-dark" onClick={() => setIsCreateModalOpen(true)}>
            <TbPlus />
            {t('addCategory')}
          </Button>
        </div>
      )}

      <div className="space-y-6">
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
        ) : sortedCategories.length === 0 ? (
          <div className="py-12 text-center">
            <TbCategory className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-sm font-medium text-gray-900">{t('emptyState.title')}</h3>
            <p className="mt-2 text-sm text-gray-500">{t('emptyState.description')}</p>
            <div className="mt-4">
              <Button variant="primary-dark" onClick={() => setIsCreateModalOpen(true)}>
                <TbPlus />
                {t('addFirstCategory')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {sortedCategories.map((category) => (
              <div
                key={category.financialCategoryId}
                className="rounded-xl border border-secondary-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-lg"
                      style={{ backgroundColor: category.color }}>
                      <TbCategory className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="flex items-center gap-1 font-semibold text-gray-900">
                        {category.name}
                        {category.isSystem && (
                          <TbLock
                            className="h-3.5 w-3.5 text-gray-400"
                            title={t('systemBadge') ?? 'System'}
                          />
                        )}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {category.isSystem ? t('systemCategory') : t('userCategory')}
                      </p>
                    </div>
                  </div>
                  {!category.isSystem && (
                    <ActionMenu>
                      <ActionMenuButton
                        icon={TbEdit}
                        text={t('edit')}
                        onClick={() => openEditModal(category)}
                      />
                      <ActionMenuButton
                        icon={TbTrash}
                        text={t('delete')}
                        onClick={() => handleDeleteCategory(category)}
                        className="text-danger hover:text-red-700"
                      />
                    </ActionMenu>
                  )}
                </div>
                {category.description && (
                  <p className="mb-4 text-sm text-gray-600">{category.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <div
                    className="h-4 w-4 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateCategoryModal
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        onCreated={handleCategoryCreated}
      />

      <ModalLayout
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('editModal.title')}</h2>
          <div className="space-y-4">
            <Input
              id="edit-category-name"
              label={t('editModal.name')}
              type="text"
              placeholder={t('editModal.namePlaceholder')}
              value={editingCategory?.name ?? ''}
              onChange={(e) =>
                setEditingCategory((prev) => (prev ? { ...prev, name: e.target.value } : null))
              }
            />
            <Labeled label={t('editModal.description')}>
              <textarea
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder={t('editModal.descriptionPlaceholder')}
                value={editingCategory?.description ?? ''}
                onChange={(e) =>
                  setEditingCategory((prev) =>
                    prev ? { ...prev, description: e.target.value } : null,
                  )
                }
              />
            </Labeled>
            <Labeled label={t('editModal.color')}>
              <div className="grid grid-cols-5 gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`h-8 w-8 rounded-full border-2 ${
                      editingCategory?.color === color ? 'border-gray-900' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() =>
                      setEditingCategory((prev) => (prev ? { ...prev, color } : null))
                    }
                  />
                ))}
              </div>
            </Labeled>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleEditCategory}>
                {t('update')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>
    </>
  );
}